import {
  type Node,
  type Edge,
  ReactFlow,
  Background,
  useEdgesState,
  useNodesState,
  ConnectionLineType,
  NodeMouseHandler,
  OnConnect,
  Handle,
  Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useCallback, useRef, useState } from "react";
import { AreaContextMenu, MenuProps } from "./AreaContextMenu";
import { getAreaType, updateArea } from "@/sideEffects/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { showApiError, showSuccess } from "@/lib/toast";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/components/theme/theme-provider";
import { NodeData, NodeDataArea } from "./AreaPageTypes";
import { nodeWidth } from "./AreaPageUtils";
import { LandPlotIcon, Tv2Icon } from "lucide-react";

interface AreaPageFlowProps {
  layoutedNodes: Node[];
  layoutedEdges: Edge[];
  mode: "edit" | "view";
  onNodeClick?: (id: number) => void;
  selectedAreaId?: number;
}

// Add proper typing for the node props
type CustomNodeProps = {
  data: NodeData;
  isConnectable: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

const nodeTypes = {
  area: ({ data, isConnectable }: CustomNodeProps) => {
    const activeFirm = useSelector(selectActiveFirm);
    if (data.type !== "area") return null;

    const { data: areaTypes } = useQuery({
      queryFn: () => getAreaType({ pageNumber: 1, numberOfObjectsPerPage: 999999 }, { Firm_Id: activeFirm?.firmGuid }),
      queryKey: ["getAreaType", activeFirm?.firmGuid],
    });

    const areaType = areaTypes?.data?.result?.find((areaType) => areaType.intId === data.areaTypeId);

    return (
      <div
        className="border border-border text-sm dark:text-black"
        style={{
          padding: "10px",
          borderRadius: "8px",
          width: nodeWidth,
          backgroundColor: areaType?.color,
          border: data.overwriteSub ? "2px solid red" : "",
        }}
      >
        <Handle type="target" position={Position.Top} isConnectable={isConnectable} />
        <div className="flex items-center flex-wrap gap-2">
          <LandPlotIcon className="w-4 h-4" /> {data.label} {areaType?.name ? `(${areaType?.name})` : ""}
        </div>

        <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} />
      </div>
    );
  },
  player: ({ data, isConnectable }: CustomNodeProps) => {
    return (
      <div
        className="border border-border text-sm bg-yellow-200 dark:bg-yellow-900"
        style={{
          padding: "10px",
          borderRadius: "8px",
          width: nodeWidth,
        }}
      >
        <Handle type="target" position={Position.Top} isConnectable={isConnectable} />
        <div className="flex items-center gap-2">
          <Tv2Icon className="w-4 h-4" /> {data.label}
        </div>
      </div>
    );
  },
};

export const AreaPageFlow: React.FC<AreaPageFlowProps> = ({
  layoutedNodes,
  layoutedEdges,
  mode,
  onNodeClick: _onNodeClick,
  selectedAreaId,
}) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const ref = useRef(null);

  const { theme } = useTheme();

  const initialNodes = layoutedNodes.map((node) => {
    return {
      ...node,
      style:
        selectedAreaId === Number(node.id)
          ? { ...node.style, border: "2px solid #1a192b", boxShadow: "0 0 10px 2px rgba(26, 25, 43, 0.3)" }
          : node.style,
    };
  });

  const [nodes, _setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, _setEdges, onEdgesChange] = useEdgesState(layoutedEdges);
  const [menu, setMenu] = useState<MenuProps>();

  const onNodeContextMenu: NodeMouseHandler<Node<NodeData>> = useCallback(
    (event, node) => {
      if (mode === "view" || node.data.type === "player") return;
      event.preventDefault();
      // @ts-ignore
      const pane = ref?.current?.getBoundingClientRect();
      setMenu({
        id: node.id,
        top: Number(event.clientY < pane.height - 200 && event.clientY),
        left: Number(event.clientX < pane.width - 200 && event.clientX),
      });
    },
    [mode, setMenu]
  );

  const onPaneClick = useCallback(() => setMenu(undefined), [setMenu]);

  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: (data: {
      id: string;
      motherAreaintId?: number;
      timezone: string;
      name: string;
      channelintId?: number;
      defaultContentintId?: number;
      areaTypeId: number;
      overwriteSub: boolean;
    }) =>
      updateArea({
        id: data.id,
        firmGuid: String(activeFirm?.firmGuid),
        name: data.name,
        timezone: data.timezone,
        motherAreaintId: data.motherAreaintId,
        defaultContentId: data.defaultContentintId,
        areaTypeId: data.areaTypeId,
        overwriteSub: data.overwriteSub,
      }),
    onSuccess: () => {
      showSuccess(t("common.updatedSuccess"));
      queryClient.invalidateQueries({ queryKey: ["area"] });
    },
    onError: showApiError,
  });

  const onConnect: OnConnect = (params) => {
    if (mode === "view") return;
    const { source: sourceIdString, target: targetIdString } = params;
    if (sourceIdString === targetIdString) return;

    const sourceNode: Node<NodeData> | undefined = nodes.find((node) => node.id === sourceIdString) as
      | Node<NodeData>
      | undefined;
    const targetNode: Node<NodeData> | undefined = nodes.find((node) => node.id === targetIdString) as
      | Node<NodeData>
      | undefined;

    if (targetNode?.data.type === "player") return;
    const sourceNodeData = sourceNode?.data as NodeDataArea;
    updateMutation.mutate({
      id: sourceNode?.data.idString as string,
      motherAreaintId: Number(targetNode?.data.intId),
      timezone: sourceNode?.data.timezone as string,
      name: sourceNode?.data.name as string,
      defaultContentintId: sourceNodeData.defaultContentintId,
      areaTypeId: sourceNodeData.areaTypeId,
      overwriteSub: sourceNodeData.overwriteSub,
    });
  };

  const onNodeClick: NodeMouseHandler<any> = useCallback(
    (event, node) => {
      if (mode === "view") {
        if (node.data.type === "area") {
          _onNodeClick?.(Number(node.id.split("-")[1]));
        }
      } else {
        if (node.data.type === "area") {
          // @ts-ignore
          const pane = ref?.current?.getBoundingClientRect();
          setMenu({
            id: node.id,
            top: Number(event.clientY < pane.height - 200 && event.clientY),
            left: Number(event.clientX < pane.width - 200 && event.clientX),
          });
        }
      }
    },
    [_onNodeClick]
  );

  const { data: areaTypesData } = useQuery({
    queryFn: () => getAreaType({ pageNumber: 1, numberOfObjectsPerPage: 999999 }, { Firm_Id: activeFirm?.firmGuid }),
    queryKey: ["getAreaType", activeFirm?.firmGuid],
  });

  const areaTypes = areaTypesData?.data?.result || [];

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <ReactFlow
        ref={ref}
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        connectionLineType={ConnectionLineType.SmoothStep}
        // @ts-ignore
        onNodeContextMenu={onNodeContextMenu as NodeMouseHandler<Node<NodeData>>}
        onPaneClick={onPaneClick}
        nodesDraggable={false}
        nodesConnectable={mode === "edit"}
        onNodeClick={onNodeClick}
        colorMode={theme === "dark" ? "dark" : "light"}
        fitView={true}
      >
        <Background />
        {menu && <AreaContextMenu {...menu} />}
        <div className="absolute top-4 left-4 w-full h-full">
          {areaTypes.map((areaType) => (
            <div key={areaType.intId} className="text-xs flex items-center gap-2">
              <div className="w-2 h-2" style={{ backgroundColor: areaType.color }} />
              {areaType.name}
            </div>
          ))}
        </div>
      </ReactFlow>
    </div>
  );
};
